import { ClipboardEvent, FC, KeyboardEvent, useEffect, useRef } from 'react';
import {
  Control,
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFormSetFocus,
  useController,
} from 'react-hook-form';
import styled from 'styled-components';

import ValidationMessage from '@/components/Forms/ValidationMessage';

import { FormDataT } from './ScriptsCard';

const Textarea = styled.textarea`
  width: 100%;
  border: 0;
  outline: none;
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.black};
  font-family: 'Manrope';
  overflow-y: hidden;
  resize: none;
  flex: 1;
`;

const StyledValidationMessage = styled(ValidationMessage)`
  display: inline-flex;
`;

const Wrap = styled.div`
  flex: 1;
  margin-right: 30px;
`;

interface Props {
  control: Control<FormDataT>;
  index: number;
  replace: UseFieldArrayReplace<FormDataT, 'script'>;
  insert: UseFieldArrayInsert<FormDataT, 'script'>;
  setFocus: UseFormSetFocus<FormDataT>;
  remove: UseFieldArrayRemove;
  fieldsCount: number;
}

const ScriptField: FC<Props> = ({
  control,
  index,
  replace,
  insert,
  setFocus,
  remove,
  fieldsCount,
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController<FormDataT, `script.${number}.text`>({
    name: `script.${index}.text`,
    control,
  });

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (value || fieldsCount > 1) {
      return;
    }

    const pastedText = e.clipboardData.getData('text');

    // [00:00.000 --> 00:03.920] For this next products[00:04.000 --> 00:05.950]Test222

    const matches = Array.from(
      pastedText.matchAll(/\[\d{2}:\d{2}.\d{3} --> \d{2}:\d{2}.\d{3}\]/g),
    );

    const blocks = matches.map<{
      text: string;
      timestamp: string;
    }>((match, index) => {
      const timestamp = match[0];

      const text = pastedText
        .substring(
          match.index! + 25,
          matches[index + 1]?.index ?? pastedText.length,
        )
        .trim();

      return {
        text,
        timestamp: timestamp.replace('[', '').replace(']', ''),
      };
    });

    if (blocks.length) {
      e.preventDefault();
      replace(blocks);
      setFocus(`script.${blocks.length - 1}.text`);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Backspace') {
      if (fieldsCount === 1) {
        return;
      }

      if (!value) {
        e.preventDefault();
        remove(index);
        const focusIndex = index === 0 ? index : index - 1;
        setFocus(`script.${focusIndex}.text`);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      insert(
        index + 1,
        {
          timestamp: '',
          text: '',
        },
        {
          shouldFocus: true,
        },
      );
    }
  };

  useEffect(() => {
    const { current: el } = inputRef;

    if (!el) {
      return;
    }

    el.style.height = '0';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);

  return (
    <Wrap>
      <Textarea
        ref={(el) => {
          ref(el);
          inputRef.current = el;
        }}
        placeholder={fieldsCount === 1 ? 'Add script here' : ''}
        value={value}
        onChange={onChange}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
      />
      {!!error && (
        <StyledValidationMessage>{error.message}</StyledValidationMessage>
      )}
    </Wrap>
  );
};

export default ScriptField;
